"use client";

import { persistAuthTokenPayload } from "@/modules/auth/services/auth.api";
import { apiClient } from "@/services/apiClient";
import type { AuthUser } from "@/modules/auth/types";

export type CreateDependantPayload = {
  fname: string;
  lname: string;
  email: string;
  gender: string;
  birthday: string | null;
  country: string;
  phone?: string;
  timezone: string;
  image?: File | null;
  remove_image?: boolean;
  terms?: boolean;
};

export type CreateDependantResponse = {
  dependant: Record<string, unknown>;
  user: AuthUser;
};

export type SwitchDependantResponse = {
  token: string;
  token_type: string;
  type: string;
  user: AuthUser;
};

function buildDependantFormData(payload: CreateDependantPayload): FormData {
  const formData = new FormData();

  formData.append("fname", payload.fname);
  formData.append("lname", payload.lname);
  formData.append("email", payload.email);
  formData.append("gender", payload.gender);
  if (payload.birthday) formData.append("birthday", payload.birthday);
  formData.append("country", payload.country);
  if (payload.phone) formData.append("phone", payload.phone);
  formData.append("timezone", payload.timezone);

  if (payload.image) {
    formData.append("image", payload.image);
  }

  if (payload.remove_image) {
    formData.append("remove_image", "1");
  }

  if (payload.terms) {
    formData.append("terms", "1");
  }

  return formData;
}

export async function createDependantApi(
  payload: CreateDependantPayload
): Promise<CreateDependantResponse> {
  const response = await apiClient.post("/api/me/dependants", buildDependantFormData(payload));

  return response.data as CreateDependantResponse;
}

export async function switchDependantApi(
  dependantId: string | number
): Promise<SwitchDependantResponse> {
  const response = await apiClient.post(`/api/me/dependants/${dependantId}/switch`);

  persistAuthTokenPayload(response.data, response.headers as Record<string, unknown>);

  return response.data as SwitchDependantResponse;
}

export async function updateDependantApi(
  dependantId: string | number,
  payload: CreateDependantPayload
): Promise<CreateDependantResponse> {
  // Use POST with _method=PUT to ensure multipart/form-data works correctly with files
  const response = await apiClient.post(
    `/api/me/dependants/${dependantId}`,
    buildDependantFormData(payload),
    { params: { _method: "PUT" } }
  );

  return response.data as CreateDependantResponse;
}

export async function getDependantApi(
  dependantId: string | number
): Promise<{ dependant: Record<string, unknown> }> {
  const response = await apiClient.get(`/api/me/dependants/${dependantId}`);

  return response.data;
}
